import {
  setCheckButtons,
  setTableButtons,
  setFormButtons
} from '@/utils/getbuttons.js'
import esign from '@/utils/pageParams/esign.js'
import { daysFormat } from '@/filters/index'
import { settlementDetailOptions } from '@/utils/contractSettlement'

const CONTRACT_AUDIT_RESULT_LABELS = {
  '1': '通过',
  '0': '驳回'
}

function contractStatusTooltip(row) {
  if (!row) return null
  const historyEvents = []
  if (row.createTime) {
    historyEvents.push({
      time: row.createTime,
      text:
        (row.createUserName || '系统') + '创建',
      order: 0
    })
  }
  let history = Array.isArray(row.auditHistory)
    ? row.auditHistory.slice()
    : []
  if (!history.length && row.auditTime) {
    history = [
      {
        auditTime: row.auditTime,
        auditorName: row.auditUserName,
        auditResult:
          row.auditResult ||
          CONTRACT_AUDIT_RESULT_LABELS[String(row.auditStatus || '')] ||
          '',
        auditOpinion: row.auditOpinion
      }
    ]
  }
  history.forEach((audit, index) => {
    const result =
      (audit &&
        (audit.auditResult ||
          CONTRACT_AUDIT_RESULT_LABELS[String(audit.auditStatus || '')])) ||
      ''
    const action = /驳回|拒绝/.test(result)
      ? '审核拒绝'
      : /通过/.test(result)
      ? '审核通过'
      : '审核'
    const opinion = String(
      (audit && audit.auditOpinion) || ''
    ).trim()
    historyEvents.push({
      time: (audit && audit.auditTime) || '',
      text:
        ((audit && audit.auditorName) || '系统') +
        action +
        (opinion ? ' ' + opinion : ''),
      order: index + 1
    })
  })
  historyEvents.sort((a, b) => {
    const timeDiff = String(a.time).localeCompare(String(b.time))
    return timeDiff || a.order - b.order
  })
  return {
    tipValue: historyEvents
      .map((event) => (event.time ? event.time + ' ' : '') + event.text)
      .join('\n'),
    popperClass: 'contract-status-tooltip'
  }
}

const partyOrgLabel = (params) => {
  if (params.orgType === 'com') return '所属企业'
  if (params.orgType === 'bigPro') return '所属工程'
  return '所属项目标段'
}

const EDITABLE_CONTRACT_STATUSES = ['草稿', '已驳回']
const TERMINATE_BUTTON_STYLE = {
  border: '1px solid #FF6A40',
  color: '#ffffff',
  background: '#FF6A40'
}
const PRIMARY_FILLED_BUTTON_STYLE = {
  border: '1px solid #4089FF',
  color: '#ffffff',
  background: '#4089FF'
}

const isContractEditableRow = (row) =>
  !!row && EDITABLE_CONTRACT_STATUSES.includes(row.status)

const currentContractRow = (params) => {
  const state = (params && params.state) || {}
  const step = (state.currentStep && state.currentStep.step) || ''
  const pageName = step.endsWith('List') ? step.slice(0, -4) : ''
  const pageRow =
    (pageName && state.currentRow && state.currentRow[pageName]) || {}
  return Object.keys(pageRow).length
    ? pageRow
    : (params && params.currentRow) || {}
}

const isCurrentContractEditable = (params) =>
  isContractEditableRow(currentContractRow(params))

const isCurrentContractTerminated = (params) =>
  currentContractRow(params).status === '已终止'

const contractAttachmentPageStatus = (params) => {
  const row = currentContractRow(params)
  const status = String((row && row.status) || '').trim()
  return ['草稿', '已驳回', '已生效'].includes(status) ? 'edit' : 'check'
}

const unitPriceActions = (kind, row) => {
  const actions = ['查看']
  if (isContractEditableRow(row)) {
    actions.push('编辑', '删除')
    actions.push({ name: '提审', style: PRIMARY_FILLED_BUTTON_STYLE })
  } else if (kind === 'piece' && row && row.status === '已生效') {
    actions.push({ name: '终止', style: TERMINATE_BUTTON_STYLE })
  }
  return actions
}

const contractAttachmentDialog = () => ({
  name: 'contractAttachment',
  title: '合同附件',
  editFun: 'contract/saveContractAttachments',
  manualUpload: true,
  formParams: {
    uid: (params) => params.currentRow.uid,
    contractFiles: [],
    fileLists: {
      contractFiles: []
    }
  },
  formData: [
    {
      key: 'contractFiles',
      label: '合同文本',
      type: 'upload',
      listType: 'text',
      fileType: 'pdf',
      url: '/static/files/file',
      showFilelist: false,
      autoUpload: false,
      limit: 20,
      accept: '.pdf,.doc,.docx',
      allowedTypes: ['pdf', 'doc', 'docx'],
      allowedTypeLabel: 'PDF、Word',
      uploadtip: '仅支持PDF或Word文档',
      span: 24
    }
  ]
})

const contractKindLabel = (kind) =>
  ({
    piece: '劳务计件合同',
    expense: '其他支出合同',
    purchase: '材料采购合同',
    sales: '产品销售合同'
  }[kind] || '合同')

const contractKindFromLabel = (label) =>
  ({
    劳务计件合同: 'piece',
    其他支出合同: 'expense',
    材料采购合同: 'purchase',
    产品销售合同: 'sales'
  }[label] || 'piece')

const contractKindFromParams = (params) => {
  const currentRow = (params && params.currentRow) || {}
  if (currentRow.contractKind) return currentRow.contractKind
  const currentTab = (params && params.state && params.state.currentTab) || {}
  return contractKindFromLabel(currentTab.label)
}

const contractPageListFun = (kind) => {
  if (kind === 'piece') return 'contract/getPieceContractList'
  if (kind === 'expense') return 'contract/getOtherExpenseContractList'
  if (kind === 'purchase') return 'contract/getPurchaseContractList'
  return 'contract/getSalesContractList'
}

const contractDrawerContractUid = (params, drawerName) => {
  const currentUids = (params.state && params.state.currentUids) || {}
  return currentUids[drawerName] || ''
}

const MONEY_UNITS = [{ label: '元', value: '元' }]

const readonlyDialogFields = (fields, params) => {
  const list = fields || []
  if (!params || params.pageStatus !== 'check') return list
  return list.map((field) =>
    field ? Object.assign({}, field, { disabled: true }) : field
  )
}

const flattenDictionaryLabels = (options) => {
  const labels = []
  const walk = (nodes) => {
    const list = nodes || []
    list.forEach((node) => {
      if (!node) return
      if (node.label) labels.push(node.label)
      if (Array.isArray(node.children) && node.children.length) {
        walk(node.children)
      }
    })
  }
  walk(options)
  return labels
}

const contractDetailFields = (kind) => {
  if (kind === 'piece') {
    return [
      {
        key: 'workItem',
        label: '作业细目',
        type: 'input',
        placeholder: '请输入作业细目',
        span: 12
      },
      {
        key: 'unit',
        label: '单位',
        type: 'select',
        options: (params) => params.dictionarys.unit,
        filterable: true,
        clearable: true,
        span: 12
      },
      {
        key: 'unitPrice',
        label: '单价',
        type: 'input',
        number: 2,
        inputButton: MONEY_UNITS[0].label,
        span: 12
      },
      {
        key: 'quantity',
        label: '数量',
        type: 'input',
        number: true,
        span: 12
      },
      {
        key: 'amount',
        label: '金额',
        type: 'input',
        disabled: true,
        inputButton: MONEY_UNITS[0].label,
        span: 12
      },
      {
        key: 'workContent',
        label: '作业内容',
        type: 'input',
        inputType: 'textarea',
        placeholder: '请输入作业细目包含的作业内容及其计量规则和质量标准',
        span: 24
      },
      {
        key: 'remark',
        label: '备注',
        type: 'input',
        inputType: 'textarea',
        span: 24
      }
    ]
  }
  if (kind === 'expense') {
    return [
      {
        key: 'workItem',
        label: '支出项目',
        type: 'input',
        placeholder: '请输入支出项目',
        span: 12
      },
      {
        key: 'workContent',
        label: '支出内容',
        type: 'input',
        inputType: 'textarea',
        span: 24
      },
      { key: 'unit', label: '单位', type: 'input', span: 12 },
      {
        key: 'unitPrice',
        label: '单价',
        type: 'input',
        number: true,
        inputButton: MONEY_UNITS[0].label,
        span: 12
      },
      {
        key: 'quantity',
        label: '数量',
        type: 'input',
        number: true,
        span: 12
      },
      {
        key: 'amount',
        label: '金额',
        type: 'input',
        disabled: true,
        inputButton: MONEY_UNITS[0].label,
        span: 12
      },
      {
        key: 'measurementRule',
        label: '计量规则',
        type: 'input',
        inputType: 'textarea',
        span: 12
      },
      {
        key: 'qualityStandard',
        label: '质量标准',
        type: 'input',
        inputType: 'textarea',
        span: 12
      },
      {
        key: 'remark',
        label: '备注',
        type: 'input',
        inputType: 'textarea',
        span: 24
      }
    ]
  }
  return [
    {
      key: kind === 'purchase' ? 'materialName' : 'productName',
      label: kind === 'purchase' ? '材料名称' : '产品名称',
      type: 'input',
      span: 12
    },
    {
      key: 'materialType',
      label: '材料类型',
      type: 'select',
      options: (params) =>
        flattenDictionaryLabels(params.dictionarys.goodsType).map(
          (label) => ({ label, value: label })
        ),
      filterable: true,
      span: 12,
      isShow: () => kind === 'purchase'
    },
    { key: 'specification', label: '规格型号', type: 'input', span: 12 },
    { key: 'unit', label: '单位', type: 'input', span: 12 },
    {
      key: 'unitPrice',
      label: '单价',
      type: 'input',
      number: true,
      inputButton: MONEY_UNITS[0].label,
      span: 12
    },
    {
      key: 'quantity',
      label: '数量',
      type: 'input',
      number: true,
      span: 12
    },
    {
      key: 'amount',
      label: '金额',
      type: 'input',
      disabled: true,
      inputButton: MONEY_UNITS[0].label,
      span: 12
    },
    {
      key: 'remark',
      label: '备注',
      type: 'input',
      inputType: 'textarea',
      span: 24
    }
  ]
}

const detailKind = (params) => {
  const currentRow =
    (params.currentRow && params.currentRow.contractKind) || ''
  const formKind =
    (params.formValues && params.formValues.contractKind) || ''
  const currentTab = (params.state && params.state.currentTab) || {}
  if (currentTab.label) return contractKindFromLabel(currentTab.label)
  return currentRow || formKind || 'purchase'
}

const contractUnitPriceValidator = (params) => {
  return (rule, value, callback) => {
    if (detailKind(params) !== 'piece') {
      callback()
      return
    }
    const text = value == null ? '' : String(value).trim()
    const number = Number(text)
    if (!text) {
      callback(new Error('请输入单价'))
      return
    }
    if (
      !Number.isFinite(number) ||
      number < 0 ||
      !/^\d+(?:\.\d{1,2})?$/.test(text)
    ) {
      callback(new Error('单价须为大于等于0且小数不超过2位的数值'))
      return
    }
    callback()
  }
}

const contractQuantityValidator = (params) => {
  return (rule, value, callback) => {
    if (detailKind(params) !== 'piece') {
      callback()
      return
    }
    const text = value == null ? '' : String(value).trim()
    const number = Number(text)
    if (!text) {
      callback(new Error('请输入数量'))
      return
    }
    if (!Number.isFinite(number) || number <= 0) {
      callback(new Error('数量须为大于0的数值'))
      return
    }
    callback()
  }
}

const formValueFromContext = (formValues, formName) =>
  formValues && formValues[formName] ? formValues[formName] : formValues || {}

const detailOptionsFromForm = (formName) => ({ formValues }) => {
  const form = formValueFromContext(formValues, formName)
  return (form.detailOptions || []).map(
    (item) => ({
      label: item.workItem || item.productName || '',
      value: item.uid
    })
  )
}

function defaultSettlementPeriod(params) {
  const year = String(new Date().getFullYear())
  const rows = (params.state.contract.pieceSettlementRows || []).filter(
    (row) => {
      const date = String((row && (row.settlementDate || row.createTime)) || '')
      return date.slice(0, 4) === year
    }
  )
  return year + '年第' + (rows.length + 1) + '期次'
}

const ledgerDetailTable = (kind) => ({
  key: 'details',
  label: kind === 'settlement' ? '结算明细' : '发货明细',
  type: 'table',
  editable: true,
  span: 24,
  headSelect:
    kind === 'settlement'
      ? {
          placeholder: '请选择要结算的合同明细',
          options: ({ formValues }) => {
            const form = formValueFromContext(
              formValues,
              'contractSettlementForm'
            )
            const selected = new Set(
              (form.details || []).map((line) =>
                String(line.contractDetailUid || '')
              )
            )
            return (form.detailOptions || [])
              .filter((item) => item && !selected.has(String(item.uid)))
              .filter(
                (item) =>
                  Number(item.contractQuantity || 0) >
                  Number(item.settledQuantity || 0)
              )
              .map((item) => ({
                label: item.workItem || item.productName || '未命名明细',
                value: item.uid
              }))
          }
        }
      : null,
  headButtons: [
    {
      name: '添加明细',
      code: kind === 'settlement' ? 'addSettlementDetail' : 'addShipmentDetail',
      type: 'primary',
      icon: 'el-icon-plus'
    }
  ],
  columns: [
    kind === 'settlement'
      ? [
          {
            prop: 'workItem',
            label: '作业细目',
            minWidth: '180px'
          },
          {
            prop: 'contractQuantity',
            label: '合同数量',
            width: '100px'
          },
          {
            prop: 'settledQuantity',
            label: '已结算数量',
            width: '110px'
          },
          {
            prop: 'quantity',
            label: '本期结算数量',
            minWidth: '130px',
            editType: 'input',
            number: 4
          },
          {
            prop: 'unitPrice',
            label: '单价',
            minWidth: '120px'
          },
          { prop: 'amount', label: '金额', minWidth: '120px' },
          {
            prop: 'remark',
            label: '备注',
            minWidth: '180px',
            editType: 'input'
          }
        ]
      : {
          prop: 'contractDetailUid',
          label: '产品',
          minWidth: '180px',
          editType: 'select',
          options: detailOptionsFromForm('contractShipmentForm')
        },
    ...(kind === 'settlement'
      ? []
      : [
          {
            prop: 'quantity',
            label: '数量',
            minWidth: '120px',
            editType: 'input',
            number: 4
          },
          {
            prop: 'unitPrice',
            label: '单价',
            minWidth: '120px',
            editType: 'input',
            number: 4
          },
          { prop: 'amount', label: '金额', minWidth: '120px' }
        ]),
    {
      label: '操作',
      width: '80px',
      buttons: [
        {
          name: '移除',
          code:
            kind === 'settlement'
              ? 'removeSettlementDetail'
              : 'removeShipmentDetail',
          type: 'text'
        }
      ]
    }
  ]
})

const contractLedgerDialogPages = () => ({
  contractDetailForm: {
    name: 'contractDetailForm',
    title: (params) =>
      (params.pageStatus === 'edit'
        ? '编辑'
        : params.pageStatus === 'check'
        ? '查看'
        : '新增') +
      contractKindLabel(detailKind(params)) +
      '明细',
    width: '50%',
    addFun: 'contract/addContractDetail',
    editFun: 'contract/editContractDetail',
    getParentFun: 'contract/loadContractDetailRows',
    formParams: {
      uid: '',
      contractUid: (params) =>
        contractDrawerContractUid(params, 'contractDetailsDrawer'),
      contractKind: (params) =>
        contractKindFromLabel(
          params.state.currentTab && params.state.currentTab.label
        ),
      workItem: '',
      workContent: '',
      materialType: '',
      materialName: '',
      productName: '',
      specification: '',
      unit: '',
      unitPrice: 0,
      unitPriceUnit: '元',
      quantity: 0,
      amount: 0,
      amountUnit: '元',
      measurementRule: '',
      qualityStandard: '',
      remark: ''
    },
    formData: (params) =>
      readonlyDialogFields(
        contractDetailFields(detailKind(params)),
        params
      ),
    formRules: {
      workItem: [
        {
          required: (params) =>
            ['piece', 'expense'].includes(detailKind(params)),
          message: (params) =>
            detailKind(params) === 'piece'
              ? '请输入作业细目'
              : '请输入支出项目',
          trigger: 'blur'
        }
      ],
      unit: [
        {
          required: (params) => detailKind(params) === 'piece',
          message: '请选择单位',
          trigger: 'change'
        }
      ],
      unitPrice: [
        { validator: contractUnitPriceValidator, trigger: 'blur' }
      ],
      quantity: [{ validator: contractQuantityValidator, trigger: 'blur' }],
      materialName: [
        {
          required: (params) => detailKind(params) === 'purchase',
          message: '请输入材料名称',
          trigger: 'blur'
        }
      ],
      productName: [
        {
          required: (params) => detailKind(params) === 'sales',
          message: '请输入产品名称',
          trigger: 'blur'
        }
      ]
    }
  },
  contractSettlementForm: {
    name: 'contractSettlementForm',
    title: (params) =>
      params.pageStatus === 'edit'
        ? '编辑结算单'
        : params.pageStatus === 'check'
        ? '查看结算单'
        : '新增结算单',
    width: '70%',
    addFun: 'contract/addContractSettlement',
    editFun: 'contract/editContractSettlement',
    getParentFun: 'contract/loadPieceSettlementRows',
    formParams: {
      uid: '',
      contractUid: (params) =>
        contractDrawerContractUid(params, 'contractSettlementsDrawer'),
      orgUid: (params) => params.orgValue,
      settlementNo: () => 'JS' + Date.now(),
      settlementPeriod: defaultSettlementPeriod,
      settlementDate: () => daysFormat(new Date()),
      amount: 0,
      deductionAmount: 0,
      actualAmount: 0,
      details: [],
      detailOptions: (params) =>
        settlementDetailOptions(
          params.state.contract.contractDetailRows || [],
          params.state.contract.pieceSettlementRows || [],
          ''
        ),
      settlementFiles: [],
      fileLists: {
        settlementFiles: []
      }
    },
    formRules: {
      settlementNo: [
        { required: true, message: '请输入结算单号', trigger: 'blur' }
      ],
      settlementPeriod: [
        { required: true, message: '请输入结算期次', trigger: 'blur' }
      ],
      settlementDate: [
        { required: true, message: '请选择结算日期', trigger: 'change' }
      ]
    },
    formData: (params) =>
      readonlyDialogFields(
        [
          {
            key: 'settlementNo',
            label: '结算单号',
            type: 'input',
            span: 12
          },
          {
            key: 'settlementPeriod',
            label: '结算期次',
            type: 'input',
            span: 12
          },
          {
            key: 'settlementDate',
            label: '结算日期',
            type: 'time',
            span: 12
          },
          {
            key: 'amount',
            label: '计件金额',
            type: 'input',
            inputButton: '元',
            disabled: true,
            span: 12
          },
          {
            key: 'deductionAmount',
            label: '扣款金额',
            type: 'input',
            number: 2,
            inputButton: '元',
            span: 12
          },
          {
            key: 'actualAmount',
            label: '结算金额',
            type: 'input',
            inputButton: '元',
            disabled: true,
            span: 12
          },
          {
            key: 'settlementFiles',
            label: '结算附件',
            type: 'upload',
            fileType: 'image',
            showFilelist: true,
            autoUpload: false,
            limit: 20,
            accept: 'image/*',
            uploadtip: '支持图片，最多20个',
            span: 24
          },
          ledgerDetailTable('settlement')
        ],
        params
      )
  },
  contractSettlementInvoicesForm: {
    name: 'contractSettlementInvoicesForm',
    title: '合同发票',
    width: '50%',
    manualUpload: true,
    editFun: 'contract/updateContractSettlementInvoices',
    getParentFun: 'contract/loadPieceSettlementRows',
    formParams: {
      uid: '',
      invoiceFiles: [],
      fileLists: {
        invoiceFiles: []
      }
    },
    formData: [
      {
        key: 'invoiceFiles',
        label: '发票文件',
        type: 'upload',
        listType: 'text',
        fileType: 'pdf',
        url: '/static/files/file',
        showFilelist: false,
        autoUpload: false,
        limit: 20,
        accept: '.jpg,.jpeg,.png,.pdf',
        allowedTypes: ['jpg', 'jpeg', 'png', 'pdf'],
        allowedTypeLabel: '图片、PDF',
        uploadtip: '仅支持图片或PDF文档',
        span: 24
      }
    ]
  },
  contractShipmentForm: {
    name: 'contractShipmentForm',
    title: (params) =>
      (params.pageStatus === 'edit' ? '编辑发货单' : '新增发货单'),
    width: '70%',
    addFun: 'contract/addContractShipment',
    editFun: 'contract/editContractShipment',
    getParentFun: 'contract/loadSalesShipmentRows',
    formParams: {
      uid: '',
      contractUid: (params) =>
        contractDrawerContractUid(params, 'contractShipmentsDrawer'),
      orgUid: (params) => params.orgValue,
      shipmentNo: () => 'FH' + Date.now(),
      shipmentDate: () => daysFormat(new Date()),
      receiverName: '',
      receiverPhone: '',
      details: [],
      detailOptions: (params) =>
        (params.state.contract.contractDetailRows || []).slice()
    },
    formRules: {
      shipmentNo: [
        { required: true, message: '请输入发货单号', trigger: 'blur' }
      ]
    },
    formData: [
      {
        key: 'shipmentNo',
        label: '发货单号',
        type: 'input',
        span: 12
      },
      {
        key: 'shipmentDate',
        label: '发货日期',
        type: 'time',
        span: 12
      },
      {
        key: 'receiverName',
        label: '收货人',
        type: 'input',
        span: 12
      },
      {
        key: 'receiverPhone',
        label: '收货电话',
        type: 'input',
        span: 12
      },
      ledgerDetailTable('shipment')
    ]
  },
  contractInvoiceForm: {
    name: 'contractInvoiceForm',
    title: (params) =>
      (params.pageStatus === 'edit' ? '编辑开票记录' : '新增开票记录'),
    width: '50%',
    addFun: 'contract/addContractInvoice',
    editFun: 'contract/editContractInvoice',
    getParentFun: 'contract/loadContractInvoiceRows',
    formParams: {
      uid: '',
      contractUid: (params) =>
        contractDrawerContractUid(params, 'contractInvoicesDrawer'),
      contractType: (params) =>
        contractKindFromLabel(
          params.state.currentTab && params.state.currentTab.label
        ),
      orgUid: (params) => params.orgValue,
      invoiceNo: '',
      invoiceDate: () => daysFormat(new Date()),
      invoiceAmount: 0,
      taxRate: 0,
      remark: ''
    },
    formRules: {
      invoiceNo: [
        { required: true, message: '请输入发票号码', trigger: 'blur' }
      ],
      invoiceAmount: [
        { required: true, message: '请输入发票金额', trigger: 'blur' }
      ]
    },
    formData: [
      {
        key: 'invoiceNo',
        label: '发票号码',
        type: 'input',
        span: 12
      },
      {
        key: 'invoiceDate',
        label: '开票日期',
        type: 'time',
        span: 12
      },
      {
        key: 'invoiceAmount',
        label: '发票金额',
        type: 'input',
        number: 2,
        span: 12
      },
      {
        key: 'taxRate',
        label: '税率(%)',
        type: 'input',
        number: 2,
        span: 12
      },
      {
        key: 'remark',
        label: '备注',
        type: 'input',
        inputType: 'textarea',
        span: 24
      }
    ]
  },
  contractAcceptanceForm: {
    name: 'contractAcceptanceForm',
    title: '生成物资验收单',
    width: '50%',
    addFun: 'contract/createContractAcceptance',
    getParentFun: 'contract/getPurchaseContractList',
    formParams: {
      uid: '',
      receiptNo: () => 'YS' + Date.now(),
      orgUid: (params) => params.orgValue,
      orgName: (params) => {
        const contractRow =
          (params.state.currentRow && params.state.currentRow.purchaseContract) ||
          {}
        return contractRow.buyerName || ''
      },
      contractUid: (params) =>
        contractDrawerContractUid(params, 'contractAcceptanceDrawer'),
      supplier: (params) => {
        const selected =
          (params.state.selection &&
            params.state.selection.contractAcceptanceDrawer) ||
          []
        return selected.length ? selected[0].supplier || '' : ''
      },
      useDate: () => daysFormat(new Date()),
      useAmount: (params) => {
        const selected =
          (params.state.selection &&
            params.state.selection.contractAcceptanceDrawer) ||
          []
        return selected.reduce(
          (sum, item) => sum + Number(item.inboundAmount || 0),
          0
        )
      },
      useReason: '',
      useDepartment: '',
      usePerson: '',
      approver: '',
      useType: '材料采购',
      detailUids: (params) => {
        const selected =
          (params.state.selection &&
            params.state.selection.contractAcceptanceDrawer) ||
          []
        return selected.map((item) => item.uid)
      }
    },
    formRules: {
      receiptNo: [
        { required: true, message: '请输入验收单号', trigger: 'blur' }
      ],
      supplier: [
        { required: true, message: '请输入供应商', trigger: 'blur' }
      ]
    },
    formData: [
      {
        key: 'receiptNo',
        label: '验收单号',
        type: 'input',
        span: 12
      },
      { key: 'supplier', label: '供应商', type: 'input', span: 12 },
      { key: 'useDate', label: '验收日期', type: 'time', span: 12 },
      {
        key: 'useAmount',
        label: '验收金额',
        type: 'input',
        number: 2,
        span: 12
      },
      { key: 'usePerson', label: '验收人', type: 'input', span: 12 },
      {
        key: 'useReason',
        label: '验收说明',
        type: 'input',
        inputType: 'textarea',
        span: 24
      }
    ]
  }
})

const contractDetailDrawerColumns = (params) => {
  const kind = contractKindFromParams(params)
  if (kind === 'piece') {
    return [
      { prop: 'workItem', label: '作业细目', minWidth: '150px' },
      { prop: 'unit', label: '单位', width: '80px' },
      { prop: 'unitPrice', label: '单价', width: '110px' },
      { prop: 'quantity', label: '数量', width: '100px' },
      { prop: 'amount', label: '金额', width: '120px' },
      { prop: 'workContent', label: '作业内容', minWidth: '240px' },
      { prop: 'remark', label: '备注', minWidth: '150px' }
    ]
  }
  if (kind === 'expense') {
    return [
      { prop: 'workItem', label: '支出项目', minWidth: '150px' },
      { prop: 'workContent', label: '支出内容', minWidth: '220px' },
      { prop: 'unit', label: '单位', width: '80px' },
      { prop: 'unitPrice', label: '单价', width: '110px' },
      { prop: 'quantity', label: '数量', width: '100px' },
      { prop: 'amount', label: '金额', width: '120px' },
      { prop: 'measurementRule', label: '计量规则', minWidth: '160px' },
      { prop: 'qualityStandard', label: '质量标准', minWidth: '160px' },
      { prop: 'remark', label: '备注', minWidth: '150px' }
    ]
  }
  if (kind === 'purchase') {
    return [
      { prop: 'materialName', label: '材料名称', minWidth: '160px' },
      { prop: 'materialType', label: '材料类型', minWidth: '130px' },
      { prop: 'specification', label: '规格型号', minWidth: '140px' },
      { prop: 'unit', label: '单位', width: '80px' },
      { prop: 'unitPrice', label: '单价', width: '110px' },
      { prop: 'quantity', label: '数量', width: '100px' },
      { prop: 'amount', label: '金额', width: '120px' },
      { prop: 'remark', label: '备注', minWidth: '150px' }
    ]
  }
  return [
    { prop: 'productName', label: '产品名称', minWidth: '160px' },
    { prop: 'specification', label: '规格型号', minWidth: '140px' },
    { prop: 'unit', label: '单位', width: '80px' },
    { prop: 'unitPrice', label: '单价', width: '110px' },
    { prop: 'quantity', label: '数量', width: '100px' },
    { prop: 'amount', label: '金额', width: '120px' },
    { prop: 'remark', label: '备注', minWidth: '150px' }
  ]
}

const contractDrawerPageActions = () => [
  {
    codes: ['amount'],
    type: 'drawerTable',
    name: 'contractDetailsDrawer',
    checkType: 'checkByUid',
    checkFun: 'contract/loadContractDetailRows'
  },
  {
    codes: ['settlementAmount'],
    type: 'drawerTable',
    name: 'contractSettlementsDrawer',
    checkType: 'checkByUid',
    checkFun: 'contract/loadPieceSettlementRows'
  },
  {
    codes: ['shipmentAmount'],
    type: 'drawerTable',
    name: 'contractShipmentsDrawer',
    checkType: 'checkByUid',
    checkFun: 'contract/loadSalesShipmentRows'
  },
  {
    codes: ['invoiceAmount'],
    type: 'drawerTable',
    name: 'contractInvoicesDrawer',
    checkType: 'checkByUid',
    checkFun: 'contract/loadContractInvoiceRows'
  },
  {
    buttonName: '验收',
    type: 'drawerTable',
    name: 'contractAcceptanceDrawer',
    checkType: 'checkByUid',
    checkFun: 'contract/loadPurchaseAcceptanceRows'
  },
  {
    buttonName: '发货',
    type: 'drawerTable',
    name: 'contractShipmentsDrawer',
    checkType: 'checkByUid',
    checkFun: 'contract/loadSalesShipmentRows'
  }
]

const contractDrawerPages = () => ({
  contractDetailsDrawer: {
    name: 'contractDetailsDrawer',
    title: (params) =>
      contractKindLabel(contractKindFromParams(params)) + '明细',
    direction: 'btt',
    size: '60%',
    emptyText: '暂时无法查询到相应数据',
    headButtons: (params) =>
      isCurrentContractEditable(params)
        ? setCheckButtons([
            { name: '添加明细', code: 'addDetail', icon: 'el-icon-plus' }
          ])
        : [],
    rowData: (params) => params.state.contract.contractDetailRows || [],
    getListFun: 'contract/loadContractDetailRows',
    getParentFun: (params) =>
      contractPageListFun(contractKindFromParams(params)),
    deleteFun: 'contract/deleteContractDetail',
    columns: (params) => {
      const columns = contractDetailDrawerColumns(params)
      columns.push({
        label: '操作',
        width: '190px',
        buttons: (params) => () => {
          const actions = isCurrentContractEditable(params)
            ? [{ name: '查看', code: 'viewDetail' }, '编辑', '删除']
            : [{ name: '查看', code: 'viewDetail' }]
          return setTableButtons(actions)
        }
      })
      return columns
    },
    detailPage: [
      {
        code: 'addDetail',
        buttonName: '添加明细',
        type: 'dialogForm',
        name: 'contractDetailForm',
        pageStatus: 'add'
      },
      {
        buttonName: '编辑',
        type: 'dialogForm',
        name: 'contractDetailForm',
        pageStatus: 'edit',
        checkType: 'checkByRow',
        checkFun: 'contract/prepareContractDetailForm'
      },
      {
        code: 'viewDetail',
        buttonName: '查看',
        type: 'dialogForm',
        name: 'contractDetailForm',
        pageStatus: 'check',
        checkType: 'checkByRow',
        checkFun: 'contract/prepareContractDetailForm'
      }
    ]
  },
  contractSettlementsDrawer: {
    name: 'contractSettlementsDrawer',
    title: '结算台账',
    direction: 'btt',
    size: '60%',
    emptyText: '暂时无法查询到相应数据',
    headButtons: (params) =>
      isCurrentContractTerminated(params)
        ? []
        : setCheckButtons([
            { name: '新增结算单', code: 'addSettlement', icon: 'el-icon-plus' }
          ]),
    rowData: (params) =>
      params.state.contract.pieceSettlementRows || [],
    getListFun: 'contract/loadPieceSettlementRows',
    getParentFun: (params) =>
      contractPageListFun(contractKindFromParams(params)),
    deleteFun: 'contract/deleteContractSettlement',
    columns: [
      { prop: 'settlementNo', label: '结算单号', minWidth: '150px' },
      { prop: 'settlementPeriod', label: '结算期次', minWidth: '140px' },
      { prop: 'settlementDate', label: '结算日期', width: '120px' },
      { prop: 'amount', label: '计件金额', width: '120px' },
      { prop: 'deductionAmount', label: '扣款金额', width: '120px' },
      { prop: 'actualAmount', label: '结算金额', width: '120px' },
      {
        prop: 'invoiceAttachment',
        label: '发票',
        width: '110px',
        drillDown: true
      },
      {
        label: '操作',
        width: '400px',
        buttons: (params) => () =>
          setTableButtons(
            isCurrentContractTerminated(params)
              ? ['查看']
              : ['查看', '编辑', '删除']
          )
      }
    ],
    detailPage: [
      {
        code: 'addSettlement',
        buttonName: '新增结算单',
        type: 'dialogForm',
        name: 'contractSettlementForm',
        pageStatus: 'add'
      },
      {
        buttonName: '编辑',
        type: 'dialogForm',
        name: 'contractSettlementForm',
        pageStatus: 'edit',
        checkType: 'checkByRow',
        checkFun: 'contract/prepareContractSettlementForm'
      },
      {
        codes: ['invoiceAttachment'],
        type: 'dialogForm',
        name: 'contractSettlementInvoicesForm',
        pageStatus: 'edit',
        checkType: 'checkByRow',
        checkFun: 'contract/prepareContractSettlementInvoices'
      },
      {
        buttonName: '查看',
        type: 'dialogForm',
        name: 'contractSettlementForm',
        pageStatus: 'check',
        checkType: 'checkByRow',
        checkFun: 'contract/prepareContractSettlementForm'
      }
    ]
  },
  contractShipmentsDrawer: {
    name: 'contractShipmentsDrawer',
    title: '发货台账',
    direction: 'btt',
    size: '50%',
    emptyText: '暂时无法查询到相应数据',
    headButtons: setCheckButtons([
      { name: '新增发货单', code: 'addShipment', icon: 'el-icon-plus' }
    ]),
    rowData: (params) => params.state.contract.salesShipmentRows || [],
    getListFun: 'contract/loadSalesShipmentRows',
    getParentFun: (params) =>
      contractPageListFun(contractKindFromParams(params)),
    deleteFun: 'contract/deleteContractShipment',
    columns: [
      { prop: 'shipmentNo', label: '发货单号', minWidth: '150px' },
      { prop: 'shipmentDate', label: '发货日期', width: '120px' },
      { prop: 'receiverName', label: '收货人', width: '120px' },
      { prop: 'receiverPhone', label: '收货电话', width: '140px' },
      { prop: 'logisticsNo', label: '物流单号', minWidth: '160px' },
      { prop: 'status', label: '状态', width: '100px' },
      {
        label: '操作',
        width: '130px',
        buttons: setTableButtons(['编辑', '删除'])
      }
    ],
    detailPage: [
      {
        code: 'addShipment',
        buttonName: '新增发货单',
        type: 'dialogForm',
        name: 'contractShipmentForm',
        pageStatus: 'add'
      },
      {
        buttonName: '编辑',
        type: 'dialogForm',
        name: 'contractShipmentForm',
        pageStatus: 'edit',
        checkType: 'checkByRow',
        checkFun: 'contract/prepareContractShipmentForm'
      }
    ]
  },
  contractInvoicesDrawer: {
    name: 'contractInvoicesDrawer',
    title: '开票台账',
    direction: 'btt',
    size: '50%',
    emptyText: '暂时无法查询到相应数据',
    headButtons: setCheckButtons([
      { name: '新增开票记录', code: 'addInvoice', icon: 'el-icon-plus' }
    ]),
    rowData: (params) => params.state.contract.contractInvoiceRows || [],
    getListFun: 'contract/loadContractInvoiceRows',
    getParentFun: (params) =>
      contractPageListFun(contractKindFromParams(params)),
    deleteFun: 'contract/deleteContractInvoice',
    columns: [
      { prop: 'invoiceNo', label: '发票号码', minWidth: '150px' },
      { prop: 'invoiceDate', label: '开票日期', width: '120px' },
      { prop: 'invoiceAmount', label: '发票金额', width: '120px' },
      { prop: 'taxRate', label: '税率(%)', width: '100px' },
      { prop: 'buyerName', label: '购买方', minWidth: '160px' },
      { prop: 'remark', label: '备注', minWidth: '160px' },
      {
        label: '操作',
        width: '130px',
        buttons: setTableButtons(['编辑', '删除'])
      }
    ],
    detailPage: [
      {
        code: 'addInvoice',
        buttonName: '新增开票记录',
        type: 'dialogForm',
        name: 'contractInvoiceForm',
        pageStatus: 'add'
      },
      {
        buttonName: '编辑',
        type: 'dialogForm',
        name: 'contractInvoiceForm',
        pageStatus: 'edit',
        checkType: 'useRowData'
      }
    ]
  },
  contractAcceptanceDrawer: {
    name: 'contractAcceptanceDrawer',
    title: '选择入库明细生成验收单',
    direction: 'btt',
    size: '50%',
    emptyText: '暂时无法查询到相应数据',
    headButtons: setCheckButtons([
      { name: '生成验收单', code: 'generateAcceptance' }
    ]),
    rowData: (params) =>
      params.state.contract.purchaseAcceptanceCandidates || [],
    defaultSelected: (params) =>
      params.state.contract.purchaseAcceptanceCandidates || [],
    getListFun: 'contract/loadPurchaseAcceptanceRows',
    getParentFun: 'contract/getPurchaseContractList',
    selectColumn: true,
    columns: [
      { prop: 'goodsName', label: '材料名称', minWidth: '150px' },
      { prop: 'model', label: '规格型号', minWidth: '130px' },
      { prop: 'goodsType', label: '材料类型', minWidth: '140px' },
      { prop: 'supplier', label: '供应商', minWidth: '140px' },
      { prop: 'inboundQuantity', label: '入库数量', width: '100px' },
      { prop: 'price', label: '单价', width: '100px' },
      { prop: 'inboundAmount', label: '入库金额', width: '110px' },
      { prop: 'inboundDate', label: '入库日期', width: '120px' }
    ],
    detailPage: [
      {
        code: 'generateAcceptance',
        buttonName: '生成验收单',
        type: 'dialogForm',
        name: 'contractAcceptanceForm',
        pageStatus: 'add'
      }
    ]
  }
})

const unitPriceContractPage = (kind, label, pageName, stepName) => ({
  name: pageName,
  theme: label,
  ...contractLedgerDialogPages(),
  ...contractDrawerPages(),
  componentTypes: [
    'navigate',
    'pageTabs',
    'check',
    'tables',
    'stepForm',
    'drawerTable',
    'dialogForm'
  ],
  getListFun:
    kind === 'piece'
      ? 'contract/getPieceContractList'
      : 'contract/getOtherExpenseContractList',
  getDetailFun: 'contract/getUnitPriceContractInfo',
  addFun: 'contract/addUnitPriceContract',
  editFun: 'contract/editUnitPriceContract',
  deleteFun: 'contract/deleteUnitPriceContract',
  headButtons: setCheckButtons(['新增']),
  detailPage: [
    ...contractDrawerPageActions(),
    {
      buttonName: '新增',
      type: 'stepForm',
      name: pageName,
      theme: 'contract',
      step: stepName,
      label: '新增' + label
    },
    {
      buttonName: '编辑',
      type: 'stepForm',
      name: pageName,
      theme: 'contract',
      step: stepName,
      label: '编辑' + label,
      checkType: 'checkByUid',
      checkFun: 'contract/getUnitPriceContractInfo'
    },
    {
      buttonName: '查看',
      type: 'stepForm',
      name: pageName,
      theme: 'contract',
      step: stepName,
      label: '查看' + label,
      checkType: 'checkByUid',
      checkFun: 'contract/getUnitPriceContractInfo'
    },
    {
      code: 'contractAttachment',
      type: 'dialogForm',
      name: 'contractAttachment',
      pageStatus: contractAttachmentPageStatus,
      checkType: 'useRowData'
    }
  ],
  queryParams: {
    orgUid: (params) => params.orgValue,
    query: '',
    status: ''
  },
  selectData: [
    {
      key: 'query',
      label: '关键词',
      type: 'input',
      placeholder:
        kind === 'piece'
          ? '合同编号/名称/劳务公司'
          : '合同编号/名称/对方单位'
    },
    {
      key: 'status',
      label: '合同状态',
      type: 'select',
      options: [
        { value: '草稿', label: '草稿' },
        { value: '待审核', label: '待审核' },
        { value: '已生效', label: '已生效' },
        { value: '已驳回', label: '已驳回' },
        { value: '已终止', label: '已终止' }
      ],
      checkByValue: true
    }
  ],
  rowData: (params) =>
    kind === 'piece'
      ? params.state.contract.pieceContractList.result || []
      : params.state.contract.otherExpenseContractList.result || [],
  rowTotal: (params) =>
    kind === 'piece'
      ? params.state.contract.pieceContractList.totalSize || 0
      : params.state.contract.otherExpenseContractList.totalSize || 0,
  columns: [
    { prop: 'contractNo', label: '合同编号', width: '170px' },
    { prop: 'contractName', label: '合同名称', width: '220px' },
    {
      prop: 'laborCompanyName',
      label: kind === 'piece' ? '劳务公司' : '对方单位',
      width: '200px'
    },
    { prop: 'signDate', label: '签订日期', width: '120px' },
    {
      prop: 'status',
      label: '状态',
      width: '90px',
      tag: [
        { label: '草稿', type: 'info' },
        { label: '待审核', type: 'warning' },
        { label: '已生效', type: 'success' },
        { label: '已驳回', type: 'danger' },
        { label: '已终止', type: 'info' }
      ],
      tooltip: contractStatusTooltip
    },
    {
      prop: 'amount',
      label: '合同金额',
      width: '120px',
      drillDown: true
    },
    {
      prop: 'settlementAmount',
      label: '结算金额',
      width: '120px',
      drillDown: true
    },
    {
      prop: 'contractAttachment',
      label: '合同附件',
      width: '220px',
      drillDown: true
    },
    {
      label: '操作',
      buttons: () => (row) =>
        setTableButtons(unitPriceActions(kind, row))
    }
  ],
  hasPagination: true,
  formParams: {
    uid: '',
    orgUid: (params) => params.orgValue,
    partyOrgUid: (params) => params.orgValue,
    partyOrgName: (params) => params.orgName,
    contractKind: kind,
    contractNo: '',
    contractName: '',
    laborCompanyUid: '',
    laborCompanyName: '',
    signDate: () => daysFormat(new Date()),
    effectiveDate: '',
    expireDate: '',
    settlementMethod: '',
    contractCategory: '',
    workContent: '',
    workLocation: '',
    paymentTerms: '',
    qualityRequirement: '',
    safetyRequirement: '',
    contactPerson: '',
    contactPhone: '',
    amount: null,
    taxRate: null,
    remarks: ''
  },
  formRules: {
    contractName: [
      { required: true, message: '请输入合同名称', trigger: 'blur' }
    ],
    contractNo: [
      { required: true, message: '请输入合同编号', trigger: 'blur' }
    ],
    workContent: [
      {
        required: true,
        message: kind === 'piece' ? '请输入作业内容' : '请输入支出内容',
        trigger: 'blur'
      }
    ],
    laborCompanyUid: [
      { required: kind === 'piece', message: '请选择劳务公司', trigger: 'change' }
    ],
    laborCompanyName: [
      { required: kind === 'expense', message: '请输入对方单位', trigger: 'blur' }
    ]
  },
  formData: {
    card: label + '基础信息',
    bottomButtons: setFormButtons(['取消', '提交']),
    fields: [
      { key: 'contractNo', label: '合同编号', type: 'input' },
      {
        key: 'contractName',
        label: '合同名称',
        type: 'input',
        span: 24
      },
      {
        key: 'partyOrgName',
        label: partyOrgLabel,
        type: 'input',
        disabled: true,
        span: 24
      },
      kind === 'piece'
        ? {
            key: 'laborCompanyUid',
            label: '劳务公司',
            type: 'select',
            options: (params) => params.state.bidSection.laborCompanyNames,
            checkByValue: true
          }
        : {
            key: 'laborCompanyName',
            label: '对方单位',
            type: 'input'
          },
      kind === 'expense'
        ? {
            key: 'contractCategory',
            label: '支出类别',
            type: 'input'
          }
        : null,
      { key: 'signDate', label: '签订日期', type: 'time' },
      kind === 'expense'
        ? { key: 'effectiveDate', label: '生效日期', type: 'time' }
        : null,
      kind === 'expense'
        ? { key: 'expireDate', label: '到期日期', type: 'time' }
        : null,
      {
        key: kind === 'piece' ? 'workContent' : 'workContent',
        label: kind === 'piece' ? '作业内容' : '支出内容',
        type: 'input',
        inputType: 'textarea',
        autosize: { minRows: 1 },
        span: 24
      },
      kind === 'piece'
        ? { key: 'workLocation', label: '作业地点', type: 'input', span: 24 }
        : null,
      {
        key: 'paymentTerms',
        label: kind === 'piece' ? '付款方式' : '付款条件',
        type: 'input',
        inputType: 'textarea',
        autosize: { minRows: 1 },
        span: 24
      },
      kind === 'piece'
        ? {
            key: 'qualityRequirement',
            label: '质量要求',
            type: 'input',
            inputType: 'textarea',
            autosize: { minRows: 1 },
            span: 24
          }
        : null,
      kind === 'piece'
        ? {
            key: 'safetyRequirement',
            label: '安全文明施工要求',
            type: 'input',
            inputType: 'textarea',
            autosize: { minRows: 1 },
            span: 24
          }
        : null,
      { key: 'contactPerson', label: '联系人', type: 'input' },
      { key: 'contactPhone', label: '联系电话', type: 'input' },
      kind === 'expense'
        ? {
            key: 'taxRate',
            label: '税率(%)',
            type: 'input',
            number: 2
          }
        : null,
      {
        key: 'amount',
        label: '合同金额',
        type: 'input',
        disabled: true
      },
      {
        key: 'remarks',
        label: '备注',
        type: 'input',
        inputType: 'textarea',
        autosize: { minRows: 1 },
        span: 24
      }
    ].filter(Boolean)
  },
  contractAttachment: contractAttachmentDialog()
})

const createContractAuditPage = (name, formParams, fields) => {
  const detailField = fields.find((field) => field.key === 'details')
  const baseFields = fields
    .filter((field) => field.key !== 'details')
    .concat([
      {
        key: 'contractAttachmentFiles',
        label: '合同附件',
        type: 'attachment',
        span: 24
      }
    ])
  return {
    name,
    componentTypes: ['navigate', 'cardForm'],
    cardFormNames: [name],
    [name]: {
      alignTop: true,
      formParams: Object.assign({}, formParams, {
        contractAttachmentFiles: []
      }),
      formRules: {},
      formData: [
        {
          card: '基础信息',
          fields: baseFields
        },
        {
          card: '合同明细',
          fields: detailField ? [detailField] : []
        }
      ],
      bottomButtons: setFormButtons(['同意', '拒绝'])
    }
  }
}

const contract = {
  pageTabs: [
    {
      label: '个人劳动合同',
      theme: 'contract',
      step: 'laborContractList',
      stepLabel: '个人劳动合同列表',
      checkFun: 'contract/getLaborContractList'
    },
    {
      label: '劳务计件合同',
      theme: 'contract',
      step: 'pieceContractList',
      stepLabel: '劳务计件合同列表',
      checkFun: 'contract/getPieceContractList'
    },
    {
      label: '其他支出合同',
      theme: 'contract',
      step: 'otherExpenseContractList',
      stepLabel: '其他支出合同列表',
      checkFun: 'contract/getOtherExpenseContractList'
    },
    {
      label: '材料采购合同',
      theme: 'contract',
      step: 'purchaseContractList',
      stepLabel: '材料采购合同列表',
      checkFun: 'contract/getPurchaseContractList'
    },
    {
      label: '产品销售合同',
      theme: 'contract',
      step: 'salesContractList',
      stepLabel: '产品销售合同列表',
      checkFun: 'contract/getSalesContractList'
    }
  ],
  laborContract: {
    name: 'laborContract',
    theme: '劳动合同',
    componentTypes: ['navigate', 'pageTabs', 'check', 'tables', 'stepForm'], // 该页面包含的组件名称，用于判断是否返回页面渲染参数
    getListFun: 'contract/getLaborContractList',
    getDetailFun: 'contract/getLaborContractInfo',
    addFun: 'contract/addLaborContract',
    editFun: 'contract/editLaborContract',
    batchReportFun: 'contract/batchReportLaborContract',
    detailPage: [
      {
        buttonNames: ['报备', '变更'],
        type: 'stepForm',
        name: 'laborContract',
        step: 'laborContractInfo',
        theme: 'contract',
        checkType: () => {
          // 待签名/被拒绝：拉当前合同详情回显；其余仍用行数据（未备案空合同）
          return (params) => {
            const row = (params && params.row) || {}
            const code = String(
              row.statusCode != null && row.statusCode !== ''
                ? row.statusCode
                : row.status || ''
            ).trim()
            if (
              code === '2' ||
              code === '4' ||
              row.status === '待签名' ||
              row.status === '被拒绝'
            ) {
              return 'checkByUid'
            }
            return 'useRowData'
          }
        },
        checkFun: 'contract/getLaborContractInfo',
        label: () => {
          return (params) => {
            if (params.code === 'add') {
              return '劳动合同报备'
            } else if (params.code === 'update') {
              return '劳动合同变更'
            }
          }
        },
        pageStatus: () => {
          return (params) => {
            if (params.code === 'add') {
              return 'edit'
            } else if (params.code === 'update') {
              return 'add'
            }
          }
        }
      },
      {
        buttonName: '批量报备',
        type: 'stepForm',
        name: 'laborContract',
        step: 'laborContractInfo',
        theme: 'contract',
        label: '劳动合同批量报备'
      },
      {
        buttonName: '查看',
        type: 'stepForm',
        name: 'laborContract',
        step: 'laborContractInfo',
        theme: 'contract',
        checkType: 'checkByUid',
        label: '查看劳动合同',
        checkFun: 'contract/getLaborContractInfo'
      },
      {
        buttonName: '合同图片',
        type: 'preview'
      }
    ],
    selectColumn: true,
    headButtons: setCheckButtons(['批量报备']),
    queryParams: {
      organUid: (params) => {
        return params.orgValue
      },
      vProjectUid: (params) => {
        return params.orgType === 'simPro' ? params.orgValue : ''
      },
      laborTeamUid: '',
      contractEffectiveTime: '',
      contractPerid: '',
      recordStatus: '',
      query: ''
    },
    selectData: [
      {
        key: 'vProjectUid',
        label: '所属项目标段',
        type: 'select',
        options: (params) => {
          return params.state.bidSection.subBidSection
        },
        checkByValue: true
      },
      {
        key: 'laborTeamUid',
        label: '所属劳务公司',
        type: 'select',
        placeholder: '请先选择项目标段后再选择',
        options: (params) => {
          return params.state.bidSection.laborCompanyNames
        },
        filterable: true,
        checkByValue: true
      },
      {
        key: 'contractEffectiveTime',
        label: '结算方式',
        type: 'select',
        options: (params) => {
          return params.dictionarys.settlementType
        }
      },
      {
        key: 'query',
        label: '关键词',
        type: 'input',
        placeholder: '姓名'
      },
      {
        key: 'contractPerid',
        label: '合同期限类型',
        type: 'select',
        options: (params) => {
          return params.dictionarys.termType
        }
      },
      {
        key: 'recordStatus',
        label: '备案状态',
        type: 'select',
        options: (params) => {
          return params.dictionarys.isEecord
        }
      }
    ],
    rowData: (params) => {
      return params.state.contract.laborContractList.resultData
    },
    rowTotal: (params) => {
      return params.state.contract.laborContractList.totalSize
    },
    columns: [
      { prop: 'personName', label: '姓名', width: '100px' },
      { prop: 'laborTeamName', label: '所属劳务公司', width: '260px' },
      {
        prop: 'isExit',
        label: '在场状态',
        width: '',
        tag: (params) => {
          return params.dictionarys.isExit
        }
      },
      {
        prop: 'status',
        label: '备案状态',
        tooltip: (row) => {
          if (!row || row.status !== '被拒绝') return null
          const mark = row.refuseMark || row.refuse_mark || row.mark || ''
          if (!mark) return null
          return { tipValue: String(mark), tipLabel: '拒绝意见：' }
        },
        tag: [
          {
            type: 'danger',
            label: '未备案'
          },
          {
            type: 'danger',
            label: '待签名'
          },
          {
            type: 'danger',
            label: '被拒绝'
          },
          {
            type: 'info',
            label: '已终止'
          },
          {
            type: 'success',
            label: '已备案'
          }
        ]
      },
      { prop: 'contractNo', label: '合同编号', width: '170px' },
      { prop: 'contractPerid', label: '合同期限类型', width: '200px' },
      { prop: 'contractEffectiveTime', label: '结算方式', width: '' },
      { prop: 'recordTime', label: '备案时间', width: '' },
      {
        prop: 'isPic',
        label: '合同图片',
        width: '160px',
        signedDoc: {
          reportCode: 'laborContractReport',
          previewLabel: '预览合同',
          imageLabel: '合同图片',
          lockedLabel: '暂未报备',
          lockWhen: 'notReported',
          saveFun: 'contract/updateLaborContractPic',
          refreshFun: 'contract/getLaborContractList',
          contractUidKey: 'uid',
          electronicSign: true,
          multi: true,
          limit: 20
        }
      },
      {
        label: '操作',
        buttons: setTableButtons(['查看', '报备', '变更']),
        width: '220px'
      }
    ],
    hasPagination: true,
    formParams: {
      uid: '',
      personUid: '',
      vProjectName: '',
      laborTeamUid: '',
      contractNo: '',
      contractPerid: '',
      signatureTime: '',
      beginTime: '',
      endTime: '',
      contractEffectiveTime: '',
      measurementUnit: '',
      priceUnit: '',
      datetime: '',
      picUid: '',
      fileLists: {
        picUid: []
      }
    },
    formRules: {
      contractPerid: [
        {
          required: true,
          message: '请选择合同期限类型',
          trigger: 'blur'
        }
      ],
      signatureTime: [
        {
          required: true,
          message: '请选择签订日期',
          trigger: 'blur'
        },
        {
          validator: (params) => {
            return params.pageStatus === 'edit' || params.pageStatus === 'batch'
              ? params.validate.validateSignatureTime
              : null
          },
          trigger: 'blur'
        }
      ],
      beginTime: [
        {
          required: (params) => {
            return (
              params.formValues &&
              params.formValues.contractPerid === '固定期限合同'
            )
          },
          message: '当合同类型为“固定期限合同”时开始时间为必填字段',
          trigger: 'blur'
        }
      ],
      endTime: [
        {
          required: (params) => {
            return (
              params.formValues &&
              params.formValues.contractPerid === '固定期限合同'
            )
          },
          message: '当合同类型为“固定期限合同”时结束时间为必填字段',
          trigger: 'blur'
        }
      ],
      contractEffectiveTime: [
        {
          required: true,
          message: '请选择结算方式',
          trigger: 'blur'
        }
      ],
      priceUnit: [
        {
          required: true,
          message: '请输入单价或月基准价',
          trigger: 'blur'
        }
      ]
    },
    formData: (params) => {
      var formData = {
        card:
          params.pageStatus === 'check' || params.pageStatus === 'batch'
            ? '劳动合同信息'
            : null,
        titles:
          params.pageStatus === 'add' || params.pageStatus === 'edit'
            ? ['人员信息核查', '合同信息登记', '合同预览上传']
            : null,
        tips:
          params.pageStatus === 'add' || params.pageStatus === 'edit'
            ? [
                {
                  step: 0,
                  text: [
                    '1、劳动合同报备前先确认合同甲乙双方选择无误；',
                    '2、劳动合同报备时合同编号自动按照系统规则生成；',
                    '3、劳动合同报备时，如果结算方式为计件、定额或包干，请在“单价（月基准价）”字段录入预估的按月结算的基准月薪，用于系统自动计算薪酬；',
                    '4、劳动合同报备时，签订日期默认为劳务人员进场日期，合同生效月份默认为劳务人员进场月份；如果为变更合同，则签订日期默认为操作日期，合同生效月份默认为操作当月。'
                  ]
                }
              ]
            : null,
        bottomButtons:
          params.pageStatus === 'add' || params.pageStatus === 'edit'
            ? (p) => {
                if (p.activeStep === 0) {
                  return setFormButtons(['下一步'])
                }
                if (p.activeStep === 1) {
                  return setFormButtons(['上一步', '提交'])
                }
                if (p.activeStep === 2) {
                  return setFormButtons(['本地上传', '电子签署'])
                }
                return []
              }
            : setFormButtons(['取消', '提交']),
        fields: [
          {
            key: params.pageStatus === 'check' ? 'contractNo' : null,
            label: '合同编号',
            type: 'input',
            check: '查看历史合同',
            checkType: 'dropdown',
            checkDropdown: (params) => {
              return params.state.contract.allLaborContracts
            }
          },
          {
            key: params.pageStatus !== 'batch' ? 'personName' : null,
            label: '劳务人员（乙方）',
            type: 'input',
            step: 0,
            disabled: true
          },
          {
            key: params.pageStatus === 'batch' ? 'personNames' : null,
            label: '劳务人员（乙方）',
            type: 'input',
            inputType: 'textarea',
            autosize: { minRows: 1 },
            disabled: true
          },
          {
            key: 'laborTeamName',
            label: '劳务公司（甲方）',
            type: 'input',
            step: 0,
            disabled: true
          },
          {
            key: 'vProjectName',
            label: '所属项目标段',
            type: 'input',
            step: 0,
            disabled: true
          },
          {
            type:
              params.pageStatus === 'add' || params.pageStatus === 'edit'
                ? 'tag'
                : null,
            step: 0,
            tag: '劳动合同报备或变更前先确认合同甲乙双方选择无误'
          },
          {
            key: 'contractPerid',
            label: '合同期限类型',
            type: 'select',
            step:
              params.pageStatus === 'add' || params.pageStatus === 'edit'
                ? 1
                : 0,
            options: params.dictionarys.termType
          },
          {
            key: 'signatureTime',
            label: '签订日期',
            type: 'time',
            step:
              params.pageStatus === 'add' || params.pageStatus === 'edit'
                ? 1
                : 0
          },
          {
            key: 'beginTime',
            label: '开始时间',
            type: 'time',
            step:
              params.pageStatus === 'add' || params.pageStatus === 'edit'
                ? 1
                : 0
          },
          {
            key: 'endTime',
            label: '结束时间',
            type: 'time',
            step:
              params.pageStatus === 'add' || params.pageStatus === 'edit'
                ? 1
                : 0
          },
          {
            key: 'contractEffectiveTime',
            label: '结算方式',
            type: 'select',
            step:
              params.pageStatus === 'add' || params.pageStatus === 'edit'
                ? 1
                : 0,
            options: params.dictionarys.settlementType
          },
          {
            key: 'measurementUnit',
            label: '计量单位',
            type: 'input',
            step:
              params.pageStatus === 'add' || params.pageStatus === 'edit'
                ? 1
                : 0,
            disabled: true
          },
          {
            key: 'priceUnit',
            label: '单价（月基准价）',
            type: 'input',
            step:
              params.pageStatus === 'add' || params.pageStatus === 'edit'
                ? 1
                : 0
          },
          {
            key: 'datetime',
            label: '合同生效时间',
            type: 'input',
            step:
              params.pageStatus === 'add' || params.pageStatus === 'edit'
                ? 1
                : 0,
            disabled: true
          },
          {
            key: params.pageStatus !== 'batch' ? 'picUid' : null,
            label: '电子劳动合同',
            type: 'signedReport',
            reportCode: 'laborContractReport',
            saveFun: 'contract/updateLaborContractPic',
            contractUidKey: 'uid',
            previewLabel: '预览合同',
            imageLabel: '合同图片',
            electronicSign: true,
            actionsInFooter:
              params.pageStatus === 'add' || params.pageStatus === 'edit',
            multi: true,
            limit: 20,
            step: params.pageStatus === 'check' ? 0 : 2
          }
        ]
      }
      return formData
    }
  },
  /** 待办：劳动合同备案/变更审核（只读回显，无步骤条） */
  laborContractAudit: {
    name: 'laborContractAudit',
    componentTypes: ['navigate', 'stepForm'],
    approveFun: 'contract/approveLaborContractFlow',
    getDetailFun: 'contract/getLaborContractInfo',
    formParams: {
      uid: '',
      flowUid: '',
      personUid: '',
      personName: '',
      vProjectName: '',
      laborTeamUid: '',
      laborTeamName: '',
      contractNo: '',
      contractPerid: '',
      signatureTime: '',
      beginTime: '',
      endTime: '',
      contractEffectiveTime: '',
      measurementUnit: '',
      priceUnit: '',
      datetime: '',
      picUid: '',
      fileLists: {
        picUid: []
      }
    },
    formData: {
      card: '合同信息登记',
      submitOnCheck: true,
      bottomButtons: setFormButtons(['同意', '拒绝']),
      fields: [
        {
          key: 'contractNo',
          label: '合同编号',
          type: 'input',
          disabled: true
        },
        {
          key: 'personName',
          label: '劳务人员（乙方）',
          type: 'input',
          disabled: true
        },
        {
          key: 'laborTeamName',
          label: '劳务公司（甲方）',
          type: 'input',
          disabled: true
        },
        {
          key: 'vProjectName',
          label: '所属项目标段',
          type: 'input',
          disabled: true
        },
        {
          key: 'contractPerid',
          label: '合同期限类型',
          type: 'input',
          disabled: true
        },
        {
          key: 'signatureTime',
          label: '签订日期',
          type: 'input',
          disabled: true
        },
        {
          key: 'beginTime',
          label: '开始时间',
          type: 'input',
          disabled: true
        },
        {
          key: 'endTime',
          label: '结束时间',
          type: 'input',
          disabled: true
        },
        {
          key: 'contractEffectiveTime',
          label: '结算方式',
          type: 'input',
          disabled: true
        },
        {
          key: 'measurementUnit',
          label: '计量单位',
          type: 'input',
          disabled: true
        },
        {
          key: 'priceUnit',
          label: '单价（月基准价）',
          type: 'input',
          disabled: true
        },
        {
          key: 'datetime',
          label: '合同生效月份',
          type: 'input',
          disabled: true
        },
        {
          type: 'tag',
          tag: '请核对合同登记信息后办理；拒绝须填写审核意见，同意后进入合同预览签署。',
          span: 24
        }
      ]
    }
  },
  /** 待办：审核同意后预览签署（无步骤条） */
  laborContractPreview: {
    name: 'laborContractPreview',
    componentTypes: ['navigate', 'stepForm'],
    getDetailFun: 'contract/getLaborContractInfo',
    formParams: {
      uid: '',
      flowUid: '',
      laborTeamUid: '',
      picUid: '',
      fileLists: {
        picUid: []
      }
    },
    formData: {
      card: '合同预览',
      submitOnCheck: true,
      bottomButtons: setFormButtons(['本地上传', '电子签署']),
      fields: [
        {
          key: 'picUid',
          label: '电子劳动合同',
          type: 'signedReport',
          reportCode: 'laborContractReport',
          saveFun: 'contract/updateLaborContractPic',
          contractUidKey: 'uid',
          previewLabel: '预览合同',
          imageLabel: '合同图片',
          electronicSign: true,
          actionsInFooter: true,
          multi: true,
          limit: 20
        },
        {
          type: 'tag',
          tag: '可本地上传签署扫描件，或电子签署后上传合同图片；上传成功后返回待办列表。',
          span: 24
        }
      ]
    }
  },
  /** 采购合同列表与表单 */
  purchaseContract: {
    name: 'purchaseContract',
    ...contractLedgerDialogPages(),
    ...contractDrawerPages(),
    componentTypes: [
      'navigate',
      'pageTabs',
      'check',
      'tables',
      'stepForm',
      'drawerTable',
      'dialogForm'
    ],
    getListFun: 'contract/getPurchaseContractList',
    getDetailFun: 'contract/getPurchaseContractInfo',
    addFun: 'contract/addPurchaseContract',
    editFun: 'contract/editPurchaseContract',
    deleteFun: 'contract/deletePurchaseContract',
    detailPage: [
      ...contractDrawerPageActions(),
      {
        buttonName: '新增',
        name: 'purchaseContract',
        type: 'stepForm',
        theme: 'contract',
        step: 'purchaseContractInfo',
        label: '新增材料采购合同'
      },
      {
        buttonName: '编辑',
        name: 'purchaseContract',
        type: 'stepForm',
        theme: 'contract',
        step: 'purchaseContractInfo',
        checkType: 'checkByUid',
        checkFun: 'contract/getPurchaseContractInfo',
        pageStatus: 'edit'
      },
      {
        buttonName: '查看',
        name: 'purchaseContract',
        type: 'stepForm',
        theme: 'contract',
        step: 'purchaseContractInfo',
        checkType: 'checkByUid',
        checkFun: 'contract/getPurchaseContractInfo',
        pageStatus: 'check'
      },
      {
        code: 'contractAttachment',
        type: 'dialogForm',
        name: 'contractAttachment',
        pageStatus: contractAttachmentPageStatus,
        checkType: 'useRowData'
      }
    ],
    headButtons: setCheckButtons(['新增']),
    queryParams: {
      orgUid: (params) => {
        return params.orgValue
      },
      query: '',
      status: '',
      category: ''
    },
    selectData: [
      {
        key: 'query',
        label: '关键词',
        type: 'input',
        placeholder: '合同编号/名称/供应商/采购方'
      },
      {
        key: 'status',
        label: '合同状态',
        type: 'select',
        options: [
          { value: '草稿', label: '草稿' },
          { value: '待审核', label: '待审核' },
          { value: '已生效', label: '已生效' },
          { value: '已驳回', label: '已驳回' }
        ],
        checkByValue: true
      },
      {
        key: 'category',
        label: '合同类别',
        type: 'input',
        placeholder: '如钢材、水泥、砂石等'
      }
    ],
    rowData: (params) => {
      return params.state.contract.purchaseContractList.result
    },
    rowTotal: (params) => {
      return params.state.contract.purchaseContractList.totalSize
    },
    columns: [
      { prop: 'contractNo', label: '合同编号', width: '170px' },
      { prop: 'contractName', label: '合同名称', width: '220px' },
      { prop: 'contractCategory', label: '合同类别', width: '140px' },
      { prop: 'supplierName', label: '供应商', width: '180px' },
      { prop: 'buyerName', label: '采购方', width: '180px' },
      {
        prop: 'amount',
        label: '合同金额',
        width: '110px',
        drillDown: true
      },
      { prop: 'signDate', label: '签订日期', width: '120px' },
      {
        prop: 'status',
        label: '状态',
        width: '90px',
        tag: [
          { label: '草稿', type: 'info' },
          { label: '待审核', type: 'warning' },
          { label: '已生效', type: 'success' },
          { label: '已驳回', type: 'danger' }
        ],
        tooltip: contractStatusTooltip
      },
      {
        prop: 'contractAttachment',
        label: '合同附件',
        width: '220px',
        drillDown: true
      },
      {
        label: '操作',
        buttons: () => (row) => {
          const actions = ['查看']
          if (isContractEditableRow(row)) {
            actions.push('编辑', '删除')
          }
          actions.push('验收')
          if (row && isContractEditableRow(row)) {
            actions.push({
              name: '提审',
              style: PRIMARY_FILLED_BUTTON_STYLE
            })
          }
          return setTableButtons(actions)
        }
      }
    ],
    hasPagination: true,
    formParams: {
      uid: '',
      orgUid: (params) => params.orgValue,
      partyOrgUid: (params) => params.orgValue,
      partyOrgName: (params) => params.orgName,
      contractNo: '',
      contractName: '',
      contractCategory: '',
      buyerName: '',
      supplierName: '',
      signDate: () => daysFormat(new Date()),
      effectiveDate: '',
      expireDate: '',
      deliveryPlace: '',
      settlementMethod: '',
      paymentTerms: '',
      currency: '人民币',
      amount: null,
      taxRate: null,
      contactPerson: '',
      contactPhone: '',
      remarks: ''
    },
    formRules: {
      contractName: [
        { required: true, message: '请输入合同名称', trigger: 'blur' }
      ],
      contractNo: [
        { required: true, message: '请输入合同编号', trigger: 'blur' }
      ],
      supplierName: [
        { required: true, message: '请输入供应商名称', trigger: 'blur' }
      ]
    },
    formData: {
      card: '材料采购合同基础信息',
      bottomButtons: setFormButtons(['取消', '提交']),
      fields: [
        { key: 'contractNo', label: '合同编号', type: 'input' },
        {
          key: 'contractName',
          label: '合同名称',
          type: 'input',
          span: 24
        },
        {
          key: 'partyOrgName',
          label: partyOrgLabel,
          type: 'input',
          disabled: true,
          span: 24
        },
        { key: 'contractCategory', label: '合同类别', type: 'input' },
        { key: 'supplierName', label: '供应商', type: 'input' },
        { key: 'buyerName', label: '采购方', type: 'input' },
        { key: 'signDate', label: '签订日期', type: 'time' },
        { key: 'effectiveDate', label: '生效日期', type: 'time' },
        { key: 'expireDate', label: '到期日期', type: 'time' },
        {
          key: 'deliveryPlace',
          label: '交货地点',
          type: 'input',
          span: 24
        },
        { key: 'settlementMethod', label: '结算方式', type: 'input' },
        { key: 'paymentTerms', label: '付款条件', type: 'input' },
        { key: 'currency', label: '币种', type: 'input' },
        {
          key: 'amount',
          label: '合同金额',
          type: 'input',
          disabled: true
        },
        {
          key: 'taxRate',
          label: '税率(%)',
          type: 'input',
          number: 2
        },
        { key: 'contactPerson', label: '联系人', type: 'input' },
        { key: 'contactPhone', label: '联系电话', type: 'input' },
        {
          key: 'remarks',
          label: '备注',
          type: 'input',
          inputType: 'textarea',
          autosize: { minRows: 1 },
          span: 24
        }
      ]
    },
    contractAttachment: contractAttachmentDialog(),
    purchaseContractForm: {
      name: 'purchaseContractForm',
      title: '采购合同',
      addFun: 'contract/addPurchaseContract',
      editFun: 'contract/editPurchaseContract',
      formParams: {
        uid: '',
        orgUid: (params) => {
          return params.orgValue
        },
        contractNo: () => {
          return 'CG' + Date.now()
        },
        contractName: '',
        contractCategory: '',
        buyerName: '',
        supplierName: '',
        signDate: '',
        effectiveDate: '',
        expireDate: '',
        deliveryPlace: '',
        settlementMethod: '',
        paymentTerms: '',
        currency: '人民币',
        amount: null,
        taxRate: null,
        contactPerson: '',
        contactPhone: '',
        remarks: '',
        details: []
      },
      formRules: {
        contractName: [
          { required: true, message: '请输入合同名称', trigger: 'blur' }
        ],
        contractNo: [
          { required: true, message: '请输入合同编号', trigger: 'blur' }
        ],
        supplierName: [
          { required: true, message: '请输入供应商名称', trigger: 'blur' }
        ],
        signDate: [
          { required: true, message: '请选择签订日期', trigger: 'blur' }
        ]
      },
      formData: [
        {
          key: 'contractName',
          label: '合同名称',
          type: 'input',
          placeholder: '请输入采购合同名称',
          span: 24
        },
        {
          key: 'contractNo',
          label: '合同编号',
          type: 'input',
          placeholder: '可修改为实际合同编号'
        },
        {
          key: 'contractCategory',
          label: '合同类别',
          type: 'input',
          placeholder: '如钢材、水泥、砂石等'
        },
        {
          key: 'supplierName',
          label: '供应商',
          type: 'input',
          placeholder: '供应商（卖方）名称'
        },
        {
          key: 'buyerName',
          label: '采购方',
          type: 'input',
          placeholder: '采购方（买方）名称'
        },
        {
          key: 'signDate',
          label: '签订日期',
          type: 'time',
          timetype: 'date',
          format: 'yyyy-MM-dd'
        },
        {
          key: 'effectiveDate',
          label: '生效日期',
          type: 'time',
          timetype: 'date',
          format: 'yyyy-MM-dd'
        },
        {
          key: 'expireDate',
          label: '到期日期',
          type: 'time',
          timetype: 'date',
          format: 'yyyy-MM-dd'
        },
        {
          key: 'deliveryPlace',
          label: '交货地点',
          type: 'input',
          span: 24
        },
        {
          key: 'settlementMethod',
          label: '结算方式',
          type: 'input',
          placeholder: '如月结、货到付款、预付款等'
        },
        {
          key: 'paymentTerms',
          label: '付款条件',
          type: 'input',
          placeholder: '如验收合格后30日内付款'
        },
        {
          key: 'currency',
          label: '币种',
          type: 'input'
        },
        {
          key: 'amount',
          label: '合同金额',
          type: 'input',
          number: 2
        },
        {
          key: 'taxRate',
          label: '税率(%)',
          type: 'input',
          number: 2
        },
        {
          key: 'contactPerson',
          label: '联系人',
          type: 'input'
        },
        {
          key: 'contactPhone',
          label: '联系电话',
          type: 'input'
        },
        {
          key: 'remarks',
          label: '备注',
          type: 'input',
          inputType: 'textarea',
          autosize: { minRows: 1 },
          span: 24
        },
        {
          key: 'details',
          label: '合同明细',
          type: 'table',
          editable: true,
          span: 24,
          maxHeight: 280,
          headButtons: setCheckButtons([
            { name: '添加明细', code: 'addPurchaseDetail' }
          ]),
          columns: [
            {
              prop: 'materialName',
              label: '原材名称',
              editType: 'input',
              minWidth: '150px'
            },
            {
              prop: 'specification',
              label: '规格型号',
              editType: 'input',
              minWidth: '140px'
            },
            {
              prop: 'unit',
              label: '单位',
              editType: 'input',
              minWidth: '80px'
            },
            {
              prop: 'quantity',
              label: '数量',
              editType: 'input',
              number: 4,
              minWidth: '100px'
            },
            {
              prop: 'unitPrice',
              label: '单价',
              editType: 'input',
              number: 4,
              minWidth: '110px'
            },
            {
              prop: 'amount',
              label: '金额',
              editType: 'input',
              number: 2,
              minWidth: '110px'
            },
            {
              prop: 'remark',
              label: '备注',
              editType: 'input',
              minWidth: '140px'
            },
            {
              label: '操作',
              width: '90px',
              buttons: setTableButtons([
                { name: '移除', code: 'removePurchaseDetail' }
              ])
            }
          ]
        }
      ]
    }
  },
  /** 销售合同列表与表单 */
  salesContract: {
    name: 'salesContract',
    ...contractLedgerDialogPages(),
    ...contractDrawerPages(),
    componentTypes: [
      'navigate',
      'pageTabs',
      'check',
      'tables',
      'stepForm',
      'drawerTable',
      'dialogForm'
    ],
    getListFun: 'contract/getSalesContractList',
    getDetailFun: 'contract/getSalesContractInfo',
    addFun: 'contract/addSalesContract',
    editFun: 'contract/editSalesContract',
    deleteFun: 'contract/deleteSalesContract',
    detailPage: [
      ...contractDrawerPageActions(),
      {
        buttonName: '新增',
        name: 'salesContract',
        type: 'stepForm',
        theme: 'contract',
        step: 'salesContractInfo',
        label: '新增产品销售合同'
      },
      {
        buttonName: '编辑',
        name: 'salesContract',
        type: 'stepForm',
        theme: 'contract',
        step: 'salesContractInfo',
        checkType: 'checkByUid',
        checkFun: 'contract/getSalesContractInfo',
        pageStatus: 'edit'
      },
      {
        buttonName: '查看',
        name: 'salesContract',
        type: 'stepForm',
        theme: 'contract',
        step: 'salesContractInfo',
        checkType: 'checkByUid',
        checkFun: 'contract/getSalesContractInfo',
        pageStatus: 'check'
      },
      {
        code: 'contractAttachment',
        type: 'dialogForm',
        name: 'contractAttachment',
        pageStatus: contractAttachmentPageStatus,
        checkType: 'useRowData'
      }
    ],
    headButtons: setCheckButtons(['新增']),
    queryParams: {
      orgUid: (params) => {
        return params.orgValue
      },
      query: '',
      status: '',
      category: ''
    },
    selectData: [
      {
        key: 'query',
        label: '关键词',
        type: 'input',
        placeholder: '合同编号/名称/买方/卖方'
      },
      {
        key: 'status',
        label: '合同状态',
        type: 'select',
        options: [
          { value: '草稿', label: '草稿' },
          { value: '待审核', label: '待审核' },
          { value: '已生效', label: '已生效' },
          { value: '已驳回', label: '已驳回' }
        ],
        checkByValue: true
      },
      {
        key: 'category',
        label: '合同类别',
        type: 'input',
        placeholder: '如构件、商品混凝土等'
      }
    ],
    rowData: (params) => {
      return params.state.contract.salesContractList.result
    },
    rowTotal: (params) => {
      return params.state.contract.salesContractList.totalSize
    },
    columns: [
      { prop: 'contractNo', label: '合同编号', width: '170px' },
      { prop: 'contractName', label: '合同名称', width: '220px' },
      { prop: 'contractCategory', label: '合同类别', width: '140px' },
      { prop: 'buyerName', label: '买方', width: '180px' },
      { prop: 'sellerName', label: '卖方', width: '180px' },
      {
        prop: 'amount',
        label: '合同金额',
        width: '110px',
        drillDown: true
      },
      { prop: 'signDate', label: '签订日期', width: '120px' },
      {
        prop: 'status',
        label: '状态',
        width: '90px',
        tag: [
          { label: '草稿', type: 'info' },
          { label: '待审核', type: 'warning' },
          { label: '已生效', type: 'success' },
          { label: '已驳回', type: 'danger' }
        ],
        tooltip: contractStatusTooltip
      },
      {
        prop: 'contractAttachment',
        label: '合同附件',
        width: '220px',
        drillDown: true
      },
      {
        label: '操作',
        buttons: () => (row) => {
          const actions = ['查看']
          if (isContractEditableRow(row)) {
            actions.push('编辑', '删除')
          }
          actions.push('发货')
          if (row && isContractEditableRow(row)) {
            actions.push({
              name: '提审',
              style: PRIMARY_FILLED_BUTTON_STYLE
            })
          }
          return setTableButtons(actions)
        }
      }
    ],
    hasPagination: true,
    formParams: {
      uid: '',
      orgUid: (params) => params.orgValue,
      partyOrgUid: (params) => params.orgValue,
      partyOrgName: (params) => params.orgName,
      contractNo: '',
      contractName: '',
      contractCategory: '',
      sellerName: '',
      buyerName: '',
      signDate: () => daysFormat(new Date()),
      effectiveDate: '',
      expireDate: '',
      deliveryPlace: '',
      settlementMethod: '',
      paymentTerms: '',
      currency: '人民币',
      amount: null,
      taxRate: null,
      contactPerson: '',
      contactPhone: '',
      remarks: ''
    },
    formRules: {
      contractName: [
        { required: true, message: '请输入合同名称', trigger: 'blur' }
      ],
      contractNo: [
        { required: true, message: '请输入合同编号', trigger: 'blur' }
      ],
      buyerName: [
        { required: true, message: '请输入买方名称', trigger: 'blur' }
      ]
    },
    formData: {
      card: '产品销售合同基础信息',
      bottomButtons: setFormButtons(['取消', '提交']),
      fields: [
        { key: 'contractNo', label: '合同编号', type: 'input' },
        {
          key: 'contractName',
          label: '合同名称',
          type: 'input',
          span: 24
        },
        {
          key: 'partyOrgName',
          label: partyOrgLabel,
          type: 'input',
          disabled: true,
          span: 24
        },
        { key: 'contractCategory', label: '合同类别', type: 'input' },
        { key: 'sellerName', label: '卖方', type: 'input' },
        { key: 'buyerName', label: '买方', type: 'input' },
        { key: 'signDate', label: '签订日期', type: 'time' },
        { key: 'effectiveDate', label: '生效日期', type: 'time' },
        { key: 'expireDate', label: '到期日期', type: 'time' },
        {
          key: 'deliveryPlace',
          label: '交货地点',
          type: 'input',
          span: 24
        },
        { key: 'settlementMethod', label: '结算方式', type: 'input' },
        { key: 'paymentTerms', label: '付款条件', type: 'input' },
        { key: 'currency', label: '币种', type: 'input' },
        {
          key: 'amount',
          label: '合同金额',
          type: 'input',
          disabled: true
        },
        {
          key: 'taxRate',
          label: '税率(%)',
          type: 'input',
          number: 2
        },
        { key: 'contactPerson', label: '联系人', type: 'input' },
        { key: 'contactPhone', label: '联系电话', type: 'input' },
        {
          key: 'remarks',
          label: '备注',
          type: 'input',
          inputType: 'textarea',
          autosize: { minRows: 1 },
          span: 24
        }
      ]
    },
    contractAttachment: contractAttachmentDialog(),
    salesContractForm: {
      name: 'salesContractForm',
      title: '销售合同',
      addFun: 'contract/addSalesContract',
      editFun: 'contract/editSalesContract',
      formParams: {
        uid: '',
        orgUid: (params) => {
          return params.orgValue
        },
        contractNo: () => {
          return 'XS' + Date.now()
        },
        contractName: '',
        contractCategory: '',
        sellerName: '',
        buyerName: '',
        signDate: '',
        effectiveDate: '',
        expireDate: '',
        deliveryPlace: '',
        settlementMethod: '',
        paymentTerms: '',
        currency: '人民币',
        amount: null,
        taxRate: null,
        contactPerson: '',
        contactPhone: '',
        remarks: '',
        details: []
      },
      formRules: {
        contractName: [
          { required: true, message: '请输入合同名称', trigger: 'blur' }
        ],
        contractNo: [
          { required: true, message: '请输入合同编号', trigger: 'blur' }
        ],
        buyerName: [
          { required: true, message: '请输入买方名称', trigger: 'blur' }
        ],
        signDate: [
          { required: true, message: '请选择签订日期', trigger: 'blur' }
        ]
      },
      formData: [
        {
          key: 'contractName',
          label: '合同名称',
          type: 'input',
          placeholder: '请输入销售合同名称',
          span: 24
        },
        {
          key: 'contractNo',
          label: '合同编号',
          type: 'input',
          placeholder: '可修改为实际合同编号'
        },
        {
          key: 'contractCategory',
          label: '合同类别',
          type: 'input',
          placeholder: '如构件、商品混凝土等'
        },
        {
          key: 'sellerName',
          label: '卖方',
          type: 'input',
          placeholder: '卖方（本公司）名称'
        },
        {
          key: 'buyerName',
          label: '买方',
          type: 'input',
          placeholder: '买方（客户）名称'
        },
        {
          key: 'signDate',
          label: '签订日期',
          type: 'time',
          timetype: 'date',
          format: 'yyyy-MM-dd'
        },
        {
          key: 'effectiveDate',
          label: '生效日期',
          type: 'time',
          timetype: 'date',
          format: 'yyyy-MM-dd'
        },
        {
          key: 'expireDate',
          label: '到期日期',
          type: 'time',
          timetype: 'date',
          format: 'yyyy-MM-dd'
        },
        {
          key: 'deliveryPlace',
          label: '交货地点',
          type: 'input',
          span: 24
        },
        {
          key: 'settlementMethod',
          label: '结算方式',
          type: 'input',
          placeholder: '如月结、现款、分期等'
        },
        {
          key: 'paymentTerms',
          label: '付款条件',
          type: 'input',
          placeholder: '如发货前付款、验收后付款等'
        },
        {
          key: 'currency',
          label: '币种',
          type: 'input'
        },
        {
          key: 'amount',
          label: '合同金额',
          type: 'input',
          number: 2
        },
        {
          key: 'taxRate',
          label: '税率(%)',
          type: 'input',
          number: 2
        },
        {
          key: 'contactPerson',
          label: '联系人',
          type: 'input'
        },
        {
          key: 'contactPhone',
          label: '联系电话',
          type: 'input'
        },
        {
          key: 'remarks',
          label: '备注',
          type: 'input',
          inputType: 'textarea',
          autosize: { minRows: 1 },
          span: 24
        },
        {
          key: 'details',
          label: '合同明细',
          type: 'table',
          editable: true,
          span: 24,
          maxHeight: 280,
          headButtons: setCheckButtons([
            { name: '添加明细', code: 'addSalesDetail' }
          ]),
          columns: [
            {
              prop: 'productName',
              label: '产品名称',
              editType: 'input',
              minWidth: '150px'
            },
            {
              prop: 'specification',
              label: '规格型号',
              editType: 'input',
              minWidth: '140px'
            },
            {
              prop: 'unit',
              label: '单位',
              editType: 'input',
              minWidth: '80px'
            },
            {
              prop: 'quantity',
              label: '数量',
              editType: 'input',
              number: 4,
              minWidth: '100px'
            },
            {
              prop: 'unitPrice',
              label: '单价',
              editType: 'input',
              number: 4,
              minWidth: '110px'
            },
            {
              prop: 'amount',
              label: '金额',
              editType: 'input',
              number: 2,
              minWidth: '110px'
            },
            {
              prop: 'remark',
              label: '备注',
              editType: 'input',
              minWidth: '140px'
            },
            {
              label: '操作',
              width: '90px',
              buttons: setTableButtons([
                { name: '移除', code: 'removeSalesDetail' }
              ])
            }
          ]
        }
      ]
    }
  },
  /** 待办：劳务计件合同审核 */
  pieceContractAudit: createContractAuditPage(
    'pieceContractAudit',
    {
      uid: '',
      flowUid: '',
      contractNo: '',
      contractName: '',
      partyOrgName: '',
      laborCompanyName: '',
      signDate: '',
      workContent: '',
      workLocation: '',
      paymentTerms: '',
      qualityRequirement: '',
      safetyRequirement: '',
      contactPerson: '',
      contactPhone: '',
      amount: null,
      remarks: '',
      details: []
    },
    [
      { key: 'contractNo', label: '合同编号', type: 'input', span: 8 },
      { key: 'contractName', label: '合同名称', type: 'input', span: 8 },
      { key: 'partyOrgName', label: partyOrgLabel, type: 'input', span: 8 },
      { key: 'laborCompanyName', label: '劳务公司', type: 'input', span: 8 },
      { key: 'signDate', label: '签订日期', type: 'input', span: 8 },
      { key: 'amount', label: '合同金额', type: 'input', span: 8 },
      {
        key: 'paymentTerms',
        label: '付款方式',
        type: 'input',
        inputType: 'textarea',
        span: 8
      },
      { key: 'workLocation', label: '作业地点', type: 'input', span: 8 },
      {
        key: 'workContent',
        label: '作业内容',
        type: 'input',
        inputType: 'textarea',
        span: 8
      },
      {
        key: 'qualityRequirement',
        label: '质量要求',
        type: 'input',
        inputType: 'textarea',
        span: 24
      },
      {
        key: 'safetyRequirement',
        label: '安全文明施工要求',
        type: 'input',
        inputType: 'textarea',
        span: 24
      },
      { key: 'contactPerson', label: '联系人', type: 'input', span: 8 },
      { key: 'contactPhone', label: '联系电话', type: 'input', span: 8 },
      {
        key: 'remarks',
        label: '备注',
        type: 'input',
        inputType: 'textarea',
        span: 8
      },
      {
        key: 'details',
        label: '计件单价明细',
        type: 'table',
        span: 24,
        columns: [
          { prop: 'workItem', label: '作业细目', minWidth: '150px' },
          { prop: 'unit', label: '单位', width: '80px' },
          { prop: 'unitPrice', label: '单价', width: '110px' },
          { prop: 'quantity', label: '数量', width: '100px' },
          { prop: 'amount', label: '金额', width: '120px' },
          { prop: 'workContent', label: '作业内容', minWidth: '240px' },
          { prop: 'remark', label: '备注', minWidth: '150px' }
        ]
      }
    ]
  ),
  /** 待办：其他支出合同审核 */
  otherExpenseContractAudit: createContractAuditPage(
    'otherExpenseContractAudit',
    {
      uid: '',
      flowUid: '',
      contractNo: '',
      contractName: '',
      partyOrgName: '',
      laborCompanyName: '',
      contractCategory: '',
      signDate: '',
      effectiveDate: '',
      expireDate: '',
      workContent: '',
      paymentTerms: '',
      contactPerson: '',
      contactPhone: '',
      taxRate: null,
      amount: null,
      remarks: '',
      details: []
    },
    [
      { key: 'contractNo', label: '合同编号', type: 'input', span: 8 },
      { key: 'contractName', label: '合同名称', type: 'input', span: 8 },
      { key: 'partyOrgName', label: partyOrgLabel, type: 'input', span: 8 },
      { key: 'laborCompanyName', label: '对方单位', type: 'input', span: 8 },
      { key: 'contractCategory', label: '支出类别', type: 'input', span: 8 },
      { key: 'signDate', label: '签订日期', type: 'input', span: 8 },
      { key: 'amount', label: '合同金额', type: 'input', span: 8 },
      { key: 'effectiveDate', label: '生效日期', type: 'input', span: 8 },
      { key: 'expireDate', label: '到期日期', type: 'input', span: 8 },
      {
        key: 'workContent',
        label: '支出内容',
        type: 'input',
        inputType: 'textarea',
        span: 24
      },
      {
        key: 'paymentTerms',
        label: '付款条件',
        type: 'input',
        inputType: 'textarea',
        span: 24
      },
      { key: 'contactPerson', label: '联系人', type: 'input', span: 8 },
      { key: 'contactPhone', label: '联系电话', type: 'input', span: 8 },
      {
        key: 'remarks',
        label: '备注',
        type: 'input',
        inputType: 'textarea',
        span: 8
      },
      { key: 'taxRate', label: '税率(%)', type: 'input', span: 8 },
      {
        key: 'details',
        label: '支出明细',
        type: 'table',
        span: 24,
        columns: [
          { prop: 'workItem', label: '支出项目', minWidth: '150px' },
          { prop: 'workContent', label: '支出内容', minWidth: '220px' },
          { prop: 'unit', label: '单位', width: '80px' },
          { prop: 'unitPrice', label: '单价', width: '110px' },
          { prop: 'quantity', label: '数量', width: '100px' },
          { prop: 'amount', label: '金额', width: '120px' },
          { prop: 'measurementRule', label: '计量规则', minWidth: '160px' },
          { prop: 'qualityStandard', label: '质量标准', minWidth: '160px' },
          { prop: 'remark', label: '备注', minWidth: '150px' }
        ]
      }
    ]
  ),
  /** 待办：采购合同审核 */
  purchaseContractAudit: createContractAuditPage(
    'purchaseContractAudit',
    {
      uid: '',
      flowUid: '',
      contractNo: '',
      contractName: '',
      partyOrgName: '',
      contractCategory: '',
      supplierName: '',
      buyerName: '',
      signDate: '',
      effectiveDate: '',
      expireDate: '',
      deliveryPlace: '',
      settlementMethod: '',
      paymentTerms: '',
      currency: '',
      amount: null,
      taxRate: null,
      contactPerson: '',
      contactPhone: '',
      remarks: '',
      details: []
    },
    [
      { key: 'contractNo', label: '合同编号', type: 'input', span: 8 },
      { key: 'contractName', label: '合同名称', type: 'input', span: 8 },
      { key: 'partyOrgName', label: partyOrgLabel, type: 'input', span: 8 },
      { key: 'contractCategory', label: '合同类别', type: 'input', span: 8 },
      { key: 'supplierName', label: '供应商', type: 'input', span: 8 },
      { key: 'buyerName', label: '采购方', type: 'input', span: 8 },
      { key: 'signDate', label: '签订日期', type: 'input', span: 8 },
      { key: 'amount', label: '合同金额', type: 'input', span: 8 },
      { key: 'effectiveDate', label: '生效日期', type: 'input', span: 8 },
      { key: 'expireDate', label: '到期日期', type: 'input', span: 8 },
      { key: 'deliveryPlace', label: '交货地点', type: 'input', span: 8 },
      { key: 'settlementMethod', label: '结算方式', type: 'input', span: 8 },
      { key: 'paymentTerms', label: '付款条件', type: 'input', span: 8 },
      { key: 'currency', label: '币种', type: 'input', span: 8 },
      { key: 'taxRate', label: '税率(%)', type: 'input', span: 8 },
      { key: 'contactPerson', label: '联系人', type: 'input', span: 8 },
      { key: 'contactPhone', label: '联系电话', type: 'input', span: 8 },
      {
        key: 'remarks',
        label: '备注',
        type: 'input',
        inputType: 'textarea',
        span: 8
      },
      {
        key: 'details',
        label: '合同明细',
        type: 'table',
        span: 24,
        columns: [
          { prop: 'materialName', label: '原材名称', minWidth: '160px' },
          { prop: 'specification', label: '规格型号', minWidth: '140px' },
          { prop: 'unit', label: '单位', width: '80px' },
          { prop: 'quantity', label: '数量', width: '100px' },
          { prop: 'unitPrice', label: '单价', width: '110px' },
          { prop: 'amount', label: '金额', width: '120px' },
          { prop: 'remark', label: '备注', minWidth: '150px' }
        ]
      }
    ]
  ),
  /** 待办：销售合同审核 */
  salesContractAudit: createContractAuditPage(
    'salesContractAudit',
    {
      uid: '',
      flowUid: '',
      contractNo: '',
      contractName: '',
      partyOrgName: '',
      contractCategory: '',
      sellerName: '',
      buyerName: '',
      signDate: '',
      effectiveDate: '',
      expireDate: '',
      deliveryPlace: '',
      settlementMethod: '',
      paymentTerms: '',
      currency: '',
      amount: null,
      taxRate: null,
      contactPerson: '',
      contactPhone: '',
      remarks: '',
      details: []
    },
    [
      { key: 'contractNo', label: '合同编号', type: 'input', span: 8 },
      { key: 'contractName', label: '合同名称', type: 'input', span: 8 },
      { key: 'partyOrgName', label: partyOrgLabel, type: 'input', span: 8 },
      { key: 'contractCategory', label: '合同类别', type: 'input', span: 8 },
      { key: 'sellerName', label: '卖方', type: 'input', span: 8 },
      { key: 'buyerName', label: '买方', type: 'input', span: 8 },
      { key: 'signDate', label: '签订日期', type: 'input', span: 8 },
      { key: 'amount', label: '合同金额', type: 'input', span: 8 },
      { key: 'effectiveDate', label: '生效日期', type: 'input', span: 8 },
      { key: 'expireDate', label: '到期日期', type: 'input', span: 8 },
      { key: 'deliveryPlace', label: '交货地点', type: 'input', span: 8 },
      { key: 'settlementMethod', label: '结算方式', type: 'input', span: 8 },
      { key: 'paymentTerms', label: '付款条件', type: 'input', span: 8 },
      { key: 'currency', label: '币种', type: 'input', span: 8 },
      { key: 'taxRate', label: '税率(%)', type: 'input', span: 8 },
      { key: 'contactPerson', label: '联系人', type: 'input', span: 8 },
      { key: 'contactPhone', label: '联系电话', type: 'input', span: 8 },
      {
        key: 'remarks',
        label: '备注',
        type: 'input',
        inputType: 'textarea',
        span: 8
      },
      {
        key: 'details',
        label: '合同明细',
        type: 'table',
        span: 24,
        columns: [
          { prop: 'productName', label: '产品名称', minWidth: '160px' },
          { prop: 'specification', label: '规格型号', minWidth: '140px' },
          { prop: 'unit', label: '单位', width: '80px' },
          { prop: 'quantity', label: '数量', width: '100px' },
          { prop: 'unitPrice', label: '单价', width: '110px' },
          { prop: 'amount', label: '金额', width: '120px' },
          { prop: 'remark', label: '备注', minWidth: '150px' }
        ]
      }
    ]
  ),
  pieceContract: unitPriceContractPage(
    'piece',
    '劳务计件合同',
    'pieceContract',
    'pieceContractInfo'
  ),
  otherExpenseContract: unitPriceContractPage(
    'expense',
    '其他支出合同',
    'otherExpenseContract',
    'otherExpenseContractInfo'
  ),
  esign
}

export default contract
